"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { isPublic } from "@/lib/profile";
import { PrivateOnlyMark } from "@/components/scene/PrivateOnlyMark";

/**
 * Screen 2.7 — Single-window clearance + Trace→Finance pillar transition.
 *
 *   At SINGLE_WINDOW_SUBMITTED, Dubai Trade clearance returns plus MOCCAE
 *   acceptance. Atlas: "Cleared. The settlement trigger just armed."
 *
 *   Split-screen handshake #4: Al Madar's view briefly shows
 *   "Shipment cleared customs · ready to release payment instruction."
 *
 * The Trace pillar ring hits 100% via a ui.PillarTransition event
 * applied in useSSE; the visual transition is driven by the HUD's
 * PillarRing reading from ev.pillarFill.
 */
export function SingleWindowReveal({
  customs,
  className,
}: {
  customs: { dubaiTrade: boolean; moccae: boolean };
  className?: string;
}) {
  const t = useTranslations("singleWindowReveal");
  const cleared = customs.dubaiTrade && customs.moccae;
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("pillarTransition")}</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">{t("title")}</h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              {isPublic() ? (
                t("publicSubtitle")
              ) : (
                <>
                  <strong className="font-bold">{t("atlasAgentLabel")}</strong> {t("privatePrefix")}{" "}
                  <PrivateOnlyMark inline>
                    <strong className="font-bold">{t("vegaAgentLabel")}</strong> {t("privateVegaClause")}
                  </PrivateOnlyMark>{" "}
                  {t("privateSuffix")}
                </>
              )}
            </p>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider",
            cleared
              ? "border-policy-pass/50 bg-policy-pass/10 text-policy-pass shadow-[0_0_24px_var(--state-traced-glow)]"
              : "border-state-traced/40 bg-state-traced/10 text-state-traced",
          )}>
            {cleared ? t("chipCleared") : t("chipAwaiting")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
          <Authority cleared={customs.dubaiTrade} name={t("dubaiTradeName")} detail={t("dubaiTradeDetail")} passLabel={t("authorityPass")} pendingLabel={t("authorityPending")} />
          <Authority cleared={customs.moccae}     name={t("moccaeName")}     detail={t("moccaeDetail")}     passLabel={t("authorityPass")} pendingLabel={t("authorityPending")} />
        </div>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-px overflow-hidden rounded-lg border border-state-financed/40 bg-state-financed/30">
        <Pane
          chip={t("auroraChip")}
          headline={t("auroraHeadline")}
          detail={t("auroraDetail")}
          tone="trace"
          handshakeBadge={t("handshakeBadge")}
        />
        <Pane
          chip={t("alMadarChip")}
          headline={t("alMadarHeadline")}
          detail={t("alMadarDetail")}
          tone="finance"
          handshakeBadge={t("handshakeBadge")}
        />
      </div>
    </div>
  );
}

function Authority({ cleared, name, detail, passLabel, pendingLabel }: { cleared: boolean; name: string; detail: string; passLabel: string; pendingLabel: string }) {
  return (
    <div className={clsx(
      "flex items-center gap-3 rounded-md border px-3 py-2",
      cleared
        ? "border-policy-pass/40 bg-policy-pass/8 text-policy-pass"
        : "border-orbitra-muted/30 bg-orbitra-ink/30 text-orbitra-muted",
    )}>
      <span className={clsx("h-2 w-2 rounded-full", cleared ? "bg-policy-pass shadow-[0_0_8px_var(--policy-pass)]" : "bg-orbitra-muted/40")} />
      <div className="flex flex-col">
        <span className="text-orbitra-paper">{name}</span>
        <span className="text-[10px] text-orbitra-muted">{detail}</span>
      </div>
      <span className="ms-auto font-mono text-[10px] uppercase tracking-wider">{cleared ? passLabel : pendingLabel}</span>
    </div>
  );
}

function Pane({
  chip,
  headline,
  detail,
  tone,
  handshakeBadge,
}: { chip: string; headline: string; detail: string; tone: "trace" | "finance"; handshakeBadge: string }) {
  return (
    <div className="relative bg-orbitra-ink/85 p-5">
      <span className="absolute start-3 top-3 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{chip}</span>
      <div className="mt-6">
        <p className={clsx(
          "font-display text-base font-semibold",
          tone === "finance" ? "text-state-financed" : "text-state-traced",
        )}>{headline}</p>
        <p className="mt-2 text-[11px] text-orbitra-teal-soft">{detail}</p>
      </div>
      <div className="absolute start-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-state-financed px-3 py-1 font-mono text-[10px] font-semibold text-orbitra-ink">
        {handshakeBadge}
      </div>
    </div>
  );
}
